import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { ConsultationProvider } from "./contexts/ConsultationContext";
import LoginPage from "./pages/LoginPage";
import StaffLoginPage from "./pages/StaffLoginPage";
import DashboardPage from "./pages/DashboardPage";
import PurchaseModule from "./pages/PurchaseModule";
import PharmacyRouter from "./pages/PharmacyRouter";
import PharmacyStoresPage from "./pages/PharmacyStoresPage";
import StoresAdminPage from "./pages/StoresAdminPage";
import SuperAdminLoginPage from "./pages/superadmin/SuperAdminLoginPage";
import SuperAdminLayout from "./pages/superadmin/SuperAdminLayout";
import SuperAdminOverview from "./pages/superadmin/SuperAdminOverview";
import TenantsPage from "./pages/superadmin/TenantsPage";
import PlansPage from "./pages/superadmin/PlansPage";
import BillingPage from "./pages/superadmin/BillingPage";
import PlatformSettingsPage from "./pages/superadmin/PlatformSettingsPage";
import TenantAdminLayout from "./pages/tenantadmin/TenantAdminLayout";
import TenantAdminOverview from "./pages/tenantadmin/OverviewPage";
import TenantAdminUsers from "./pages/tenantadmin/UsersPage";
import TenantAdminBilling from "./pages/tenantadmin/BillingPage";
import TenantAdminDomains from "./pages/tenantadmin/DomainsPage";
import TenantAdminBranding from "./pages/tenantadmin/BrandingPage";
import TenantAdminSettings from "./pages/tenantadmin/SettingsPage";
import TenantAdminCapabilities from "./pages/tenantadmin/CapabilitiesPage";
import RolesPage from "./modules/admin-rbac/pages/RolesPage";
import PermissionsPage from "./modules/admin-rbac/pages/PermissionsPage";
import SpecialisationsPage from "./modules/admin-rbac/pages/SpecialisationsPage";
import SignupPage from "./pages/SignupPage";
import SignupSuccessPage from "./pages/SignupSuccessPage";
import SelfRegisterPage from "./modules/self-register/pages/SelfRegisterPage";
import ReceptionPage from "./pages/ReceptionPage";
import ReceptionHomePage from "./pages/ReceptionHomePage";
import InventoryPage from "./pages/InventoryPage";
import PublicClinicLandingPage from "./pages/PublicClinicLandingPage";
import PatientProfilePage from "./pages/PatientProfilePage";
import ReceptionReportsPage from "./pages/ReceptionReportsPage";
import RosterPlannerPage from "./modules/roster/pages/RosterPlannerPage";
import ManagementDashboard from "./pages/management/ManagementDashboard";
import AuthAuditPage from "./modules/admin-reports/pages/AuthAuditPage";
import PatientReferralPortalPage from "./modules/referral/pages/PatientReferralPortalPage";
import PatientReferralNewPage from "./modules/referral/pages/PatientReferralNewPage";
import PatientReferralDetailPage from "./modules/referral/pages/PatientReferralDetailPage";
import PatientHomePage from "./modules/patient-portal/pages/PatientHomePage";
import PatientAppointmentsPage from "./modules/patient-portal/pages/PatientAppointmentsPage";
import PatientBookAppointmentPage from "./modules/patient-portal/pages/PatientBookAppointmentPage";
import PatientFamilyPage from "./modules/patient-portal/pages/PatientFamilyPage";
import FacultyStaffGate from "./modules/patient-portal/components/FacultyStaffGate";
import PatientCareHistoryPage from "./modules/patient-portal/pages/PatientCareHistoryPage";
import DoctorReferralInboxPage from "./modules/referral/pages/DoctorReferralInboxPage";
import DoctorReferralLetterPage from "./modules/referral/pages/DoctorReferralLetterPage";
import PublicReferralCopyPage from "./modules/referral/pages/PublicReferralCopyPage";
import PublicAppointmentPage from "./pages/PublicAppointmentPage";
import { Toaster } from "./components/ui/sonner";
import IithBrandRoot from "./components/IithBrandRoot";
import ProtectedRoute from "./components/ProtectedRoute";
import { Loader2 } from "lucide-react";
import { installAuthInterceptor } from "./utils/axiosAuth";
import { homeForRole } from "./utils/homeForRole";
import { SINGLE_TENANT } from "./config/singleTenant";
import { managementAdminRoutes } from "./routes/managementAdminRoutes";

installAuthInterceptor();

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-700 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  if (user && user !== false) {
    return <Navigate to={homeForRole(user.role, user)} replace />;
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();
  useEffect(() => {
    const p = location.pathname;
    const suffix =
      p.startsWith('/superadmin')  ? 'Platform' :
      p.startsWith('/login')         ? 'Sign In' :
      p.startsWith('/staff/login')   ? 'Staff Sign In' :
      p.startsWith('/pharmacy/login')? 'Pharmacy Sign In' :
      p.startsWith('/purchase/login')? 'Purchase Sign In' :
      p.startsWith('/dashboard')     ? 'Dashboard' :
      p.startsWith('/pharmacy-stores')? 'Pharmacy Stores' :
      p.startsWith('/stores-admin')  ? 'Stores Admin' :
      p.startsWith('/purchase')      ? 'Purchase' :
      p.startsWith('/pharmacy')      ? 'Pharmacy' :
      p.startsWith('/consultation')  ? 'Consultation' :
      '';
    document.title = suffix ? `EMR | eCare360 · ${suffix}` : 'EMR | eCare360';
  }, [location.pathname]);
  return (
    <Routes>
      {/* SaaS super-admin */}
      <Route path="/superadmin/login" element={<SuperAdminLoginPage />} />
      <Route path="/superadmin" element={<SuperAdminLayout />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<SuperAdminOverview />} />
        <Route path="tenants" element={<TenantsPage />} />
        <Route path="plans" element={<PlansPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="settings" element={<PlatformSettingsPage />} />
      </Route>

      {/* Public clinic marketing landing — /c/<slug> */}
      <Route path="/c/:slug" element={<PublicClinicLandingPage />} />
      {/* QR digital referral copy — no auth */}
      <Route path="/r/:token" element={<PublicReferralCopyPage />} />
      {/* Public appointment card from email / QR — opaque token (anti-IDOR) */}
      <Route path="/a/:token" element={<PublicAppointmentPage />} />

      {/* Tenant-admin portal — admin role OR admin.* / roster permissions (e.g. FIC) */}
      <Route
        path="/admin"
        element={(
          <ProtectedRoute
            allowedRoles={["admin", "super_admin", "management"]}
            anyPermission={[
              "admin.users", "admin.roles", "admin.permissions",
              "admin.specialisations", "roster.view", "roster.manage",
              "audit.auth.view", "management.view",
            ]}
          >
            <TenantAdminLayout />
          </ProtectedRoute>
        )}
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<TenantAdminOverview />} />
        <Route path="users" element={<TenantAdminUsers />} />
        <Route path="roles" element={<RolesPage />} />
        <Route path="permissions" element={<PermissionsPage />} />
        <Route path="specialisations" element={<SpecialisationsPage />} />
        <Route path="roster" element={<RosterPlannerPage />} />
        <Route path="auth-audit" element={<AuthAuditPage />} />
        <Route path="billing" element={<TenantAdminBilling />} />
        <Route path="branding" element={<TenantAdminBranding />} />
        <Route path="capabilities" element={<TenantAdminCapabilities />} />
        <Route path="domains" element={<TenantAdminDomains />} />
        <Route path="settings" element={<TenantAdminSettings />} />
      </Route>

      {/* Public self-serve signup */}
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/signup/success" element={<SignupSuccessPage />} />
      {/* Campus user self-registration (affiliations) */}
      <Route path="/register" element={<SelfRegisterPage />} />

      {/* Tenant-facing app */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/staff/login" element={<PublicRoute><StaffLoginPage /></PublicRoute>} />
      <Route path="/pharmacy/login" element={<PublicRoute><StaffLoginPage role="pharmacy" /></PublicRoute>} />
      <Route path="/purchase/login" element={<PublicRoute><StaffLoginPage role="purchase" /></PublicRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute allowedRoles={["doctor","admin"]}><DashboardPage /></ProtectedRoute>} />
      <Route path="/pharmacy" element={<ProtectedRoute allowedRoles={["pharmacy","pharmacist","receptiondesk","admin"]}><PharmacyRouter /></ProtectedRoute>} />
      <Route path="/purchase" element={<ProtectedRoute allowedRoles={["purchase"]}><PurchaseModule /></ProtectedRoute>} />
      <Route path="/pharmacy-stores" element={<ProtectedRoute allowedRoles={["pharmacystores"]}><PharmacyStoresPage /></ProtectedRoute>} />
      <Route path="/stores-admin" element={<ProtectedRoute allowedRoles={["storesadmin"]}><StoresAdminPage /></ProtectedRoute>} />
      <Route path="/reception" element={<ProtectedRoute allowedRoles={["receptiondesk","admin","doctor"]}><ReceptionPage /></ProtectedRoute>} />
      <Route path="/reception/home" element={<ProtectedRoute allowedRoles={["receptiondesk","admin","doctor"]}><ReceptionHomePage /></ProtectedRoute>} />
      <Route
        path="/reception/reports"
        element={(
          <ProtectedRoute
            allowedRoles={["receptiondesk", "admin", "doctor", "management"]}
            anyPermission={["reports.view"]}
          >
            <ReceptionReportsPage />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/reception/roster"
        element={(
          <ProtectedRoute
            allowedRoles={["receptiondesk", "admin", "management"]}
            anyPermission={["roster.view"]}
          >
            <RosterPlannerPage />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/management"
        element={(
          <ProtectedRoute
            allowedRoles={["management", "admin", "receptiondesk", "super_admin"]}
            anyPermission={["management.view"]}
          >
            <ManagementDashboard />
          </ProtectedRoute>
        )}
      />
      {managementAdminRoutes()}
      <Route path="/inventory" element={<ProtectedRoute allowedRoles={["admin","doctor","receptiondesk","pharmacist","pharmacy"]}><InventoryPage /></ProtectedRoute>} />
      <Route path="/patients/:id" element={<ProtectedRoute allowedRoles={["doctor","admin","receptiondesk","pharmacist","pharmacy"]}><PatientProfilePage /></ProtectedRoute>} />
      <Route path="/patient" element={<ProtectedRoute allowedRoles={["patient"]}><PatientHomePage /></ProtectedRoute>} />
      <Route path="/patient/appointments" element={<ProtectedRoute allowedRoles={["patient"]}><PatientAppointmentsPage /></ProtectedRoute>} />
      <Route path="/patient/appointments/book" element={<ProtectedRoute allowedRoles={["patient"]}><PatientBookAppointmentPage /></ProtectedRoute>} />
      <Route path="/patient/family" element={<ProtectedRoute allowedRoles={["patient"]}><FacultyStaffGate><PatientFamilyPage /></FacultyStaffGate></ProtectedRoute>} />
      <Route path="/patient/care" element={<ProtectedRoute allowedRoles={["patient"]}><PatientCareHistoryPage /></ProtectedRoute>} />
      <Route path="/patient/referrals" element={<ProtectedRoute allowedRoles={["patient"]}><FacultyStaffGate><PatientReferralPortalPage /></FacultyStaffGate></ProtectedRoute>} />
      <Route path="/patient/referrals/new" element={<ProtectedRoute allowedRoles={["patient"]}><FacultyStaffGate><PatientReferralNewPage /></FacultyStaffGate></ProtectedRoute>} />
      <Route path="/patient/referrals/:id" element={<ProtectedRoute allowedRoles={["patient"]}><FacultyStaffGate><PatientReferralDetailPage /></FacultyStaffGate></ProtectedRoute>} />
      <Route path="/doctor/referrals" element={<ProtectedRoute allowedRoles={["doctor","admin","management"]}><DoctorReferralInboxPage /></ProtectedRoute>} />
      <Route path="/doctor/referrals/:id" element={<ProtectedRoute allowedRoles={["doctor","admin","management"]}><DoctorReferralLetterPage /></ProtectedRoute>} />
      <Route path="/" element={<HomeEntry />} />
      <Route path="*" element={<HomeEntry />} />
    </Routes>
  );
}

function HomeEntry() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (user && user !== false) return <Navigate to={homeForRole(user.role, user)} replace />;
  // One-clinic mode: public website is the home page.
  if (SINGLE_TENANT) return <PublicClinicLandingPage />;
  return <Navigate to={`/login${location.search}`} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <TenantProvider>
        <IithBrandRoot>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-right" />
          </AuthProvider>
        </IithBrandRoot>
      </TenantProvider>
    </BrowserRouter>
  );
}

export default App;
